/**
 * Masters Vuex Actions
 * Async operations for the master tag import feature.
 */

import masterImportService from '../services/masterImportService';
import masterApiDataSourceService from '../services/masterApiDataSourceService';
import Vue from 'vue';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';
import { buildImportRequest } from './utils/importHelpers';
import { FACILITY_REQUEST } from '@/utils/http/RequestRegistry';

export default {
  // ==========================================================================
  // Batch Management
  // ==========================================================================

  /**
   * Fetch paginated list of import batches.
   */
  async FETCH_BATCHES({ commit, state }, params = {}) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const query = {
        page: state.options.page,
        itemsPerPage: state.options.itemsPerPage,
        sortBy: state.options.sortBy?.[0] || 'createDate',
        sortDesc: state.options.sortDesc?.[0] ?? true,
        ...params,
      };

      const result = await masterImportService.getBatches(query);
      commit('SET_BATCHES', result.items || []);
      commit('SET_BATCHES_TOTAL', result.total || 0);
      return result;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch batches');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Fetch single batch with schema details.
   */
  async FETCH_BATCH({ commit }, { batchId, cancelToken } = {}) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const batch = await masterImportService.getBatch(batchId, { cancelToken });
      commit('SET_CURRENT_BATCH', batch);
      return batch;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch batch');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Fetch paginated tags from a specific batch.
   */
  async FETCH_BATCH_TAGS({ commit }, { batchId, pagination = {}, cancelToken } = {}) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const result = await masterImportService.getBatchTags(batchId, pagination, { cancelToken });
      commit('SET_CURRENT_BATCH_TAGS', result.items || []);
      commit('SET_CURRENT_BATCH_TAGS_TOTAL', result.total || 0);
      return result;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to fetch batch tags');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Delete a batch and its unharvested tags.
   */
  async DELETE_BATCH({ commit }, batchId) {
    commit('SET_DELETING', true);
    commit('CLEAR_ERROR');

    try {
      await masterImportService.deleteBatch(batchId);
      commit('REMOVE_BATCH', batchId);
      Vue.prototype.$toast?.success('Batch deleted successfully');
      return true;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to delete batch');
      Vue.prototype.$toast?.error('Failed to delete batch');
      throw error;
    } finally {
      commit('SET_DELETING', false);
    }
  },

  // ==========================================================================
  // Import Operations
  // ==========================================================================

  /**
   * Check for duplicate tag names before import.
   */
  async CHECK_DUPLICATES({ commit, state }) {
    commit('CLEAR_ERROR');

    try {
      const tagNames = state.parsedData
        .map(row => row[state.columnMappings['name']])  // camelCase key
        .filter(Boolean);

      if (tagNames.length === 0) {
        commit('SET_DUPLICATE_CHECK_RESULT', { totalChecked: 0, duplicatesFound: 0, duplicateNames: [] });
        return;
      }

      const result = await masterImportService.checkDuplicates({
        targetType: state.selectedScopeType,
        targetId: state.selectedTargetId,
        tagType: state.selectedTagType,
        tagNames,
      });

      commit('SET_DUPLICATE_CHECK_RESULT', result);
      return result;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to check duplicates');
      throw error;
    }
  },

  /**
   * Import master tags from parsed Excel data.
   */
  async IMPORT_MASTERS({ commit, state, dispatch }) {
    commit('SET_IMPORTING', true);
    commit('CLEAR_ERROR');

    try {
      const request = buildImportRequest(state);
      const result = await masterImportService.importMasterTags(request);

      commit('SET_IMPORT_RESULT', result);

      // Handle different result statuses
      if (result.status === 'Queued') {
        // Large import queued for background processing
        commit('SET_PENDING_IMPORT_JOB', result.jobId);
        Vue.prototype.$toast?.info('Import queued for background processing');
      } else if (result.status === 'Success' || result.status === 'PartialSuccess') {
        // Synchronous import completed
        await dispatch('FETCH_BATCHES');
        const msg = result.status === 'Success'
          ? `Successfully imported ${result.successCount} tags`
          : `Imported ${result.successCount} tags (${result.skippedCount} skipped, ${result.failedCount} failed)`;
        Vue.prototype.$toast?.success(msg);
      } else {
        Vue.prototype.$toast?.error('Import failed');
      }

      return result;
    } catch (error) {
      commit('SET_ERROR', error.message || 'Failed to import masters');
      Vue.prototype.$toast?.error(error.message || 'Import failed');
      throw error;
    } finally {
      commit('SET_IMPORTING', false);
    }
  },

  /**
   * Import master tags with SSE streaming for real-time progress updates.
   * Provides progress callbacks during import for UI feedback.
   */
  async IMPORT_MASTERS_STREAM({ commit, dispatch }, request) {
    commit('SET_IMPORTING', true);
    commit('CLEAR_ERROR');
    commit('CLEAR_IMPORT_PROGRESS');

    try {
      const result = await masterImportService.importMasterTagsStream(
        request,
        // Progress callback - updates Vuex state for reactive UI
        (progress) => {
          commit('SET_IMPORT_PROGRESS', progress);
        }
      );

      commit('SET_IMPORT_RESULT', result);
      commit('CLEAR_IMPORT_PROGRESS'); // Clear progress on completion

      // Handle different result statuses
      if (result.status === 'Success' || result.status === 'PartialSuccess') {
        await dispatch('FETCH_BATCHES');
        const msg = result.status === 'Success'
          ? `Successfully imported ${result.successCount} tags`
          : `Imported ${result.successCount} tags (${result.skippedCount} skipped, ${result.failedCount} failed)`;
        Vue.prototype.$toast?.success(msg);
      } else {
        Vue.prototype.$toast?.error('Import failed');
      }

      return result;
    } catch (err) {
      commit('SET_ERROR', err.message || 'Failed to import masters');
      commit('CLEAR_IMPORT_PROGRESS');
      Vue.prototype.$toast?.error(err.message || 'Import failed');
      throw err;
    } finally {
      commit('SET_IMPORTING', false);
    }
  },

  /**
   * Handle SignalR notification when background import completes.
   * Called from the SignalR plugin when 'MasterImportComplete' is received.
   */
  async HANDLE_IMPORT_COMPLETE({ commit, dispatch }, payload) {
    // Clear pending job
    commit('SET_PENDING_IMPORT_JOB', null);
    commit('SET_IMPORT_RESULT', payload);

    // Show notification to user
    const msg = `Import "${payload.batchName}" completed: ${payload.successCount} tags imported`;
    Vue.prototype.$toast?.success(msg);

    // Refresh batches list
    await dispatch('FETCH_BATCHES');

    // Reset wizard state
    commit('RESET_WIZARD_STATE');
  },

  // ==========================================================================
  // Wizard Actions
  // ==========================================================================

  /**
   * Open the import wizard.
   */
  OPEN_WIZARD({ commit }) {
    commit('RESET_WIZARD_STATE');
    commit('SET_WIZARD_OPEN', true);
  },

  /**
   * Close the import wizard.
   */
  CLOSE_WIZARD({ commit }) {
    commit('SET_WIZARD_OPEN', false);
    commit('RESET_WIZARD_STATE');
  },

  /**
   * Go to next wizard step.
   */
  NEXT_STEP({ commit, state }) {
    if (state.wizardStep < 4) {
      commit('SET_WIZARD_STEP', state.wizardStep + 1);
    }
  },

  /**
   * Go to previous wizard step.
   */
  PREV_STEP({ commit, state }) {
    if (state.wizardStep > 1) {
      commit('SET_WIZARD_STEP', state.wizardStep - 1);
    }
  },

  /**
   * Set parsed Excel data and columns.
   */
  SET_EXCEL_DATA({ commit }, { data, columns, fileName }) {
    commit('SET_PARSED_DATA', data);
    commit('SET_EXCEL_COLUMNS', columns);
    commit('SET_SOURCE_FILE_NAME', fileName);

    // Auto-generate batch name from filename
    if (fileName) {
      const baseName = fileName.replace(/\.[^/.]+$/, ''); // Remove extension
      const date = new Date().toISOString().split('T')[0];
      commit('SET_BATCH_NAME', `${baseName} - ${date}`);
    }
  },

  // ==========================================================================
  // Tags Dialog Actions
  // ==========================================================================

  /**
   * Open tags dialog for a batch.
   */
  async OPEN_TAGS_DIALOG({ commit, dispatch }, batch) {
    commit('SET_CURRENT_BATCH', batch);
    commit('SET_TAGS_DIALOG_OPEN', true);
    await dispatch('FETCH_BATCH_TAGS', { batchId: batch.id, pagination: { page: 1, itemsPerPage: 15 } });
  },

  /**
   * Close tags dialog.
   */
  CLOSE_TAGS_DIALOG({ commit }) {
    commit('SET_TAGS_DIALOG_OPEN', false);
    commit('RESET_BATCH_TAGS');
  },

  // ==========================================================================
  // Pagination Actions
  // ==========================================================================

  /**
   * Update pagination options and refresh data.
   */
  async UPDATE_OPTIONS({ commit, dispatch }, options) {
    commit('SET_OPTIONS', options);
    await dispatch('FETCH_BATCHES');
  },

  // ==========================================================================
  // Schema Cache Actions
  // ==========================================================================

  /**
   * Load all custom attribute schemas for a facility.
   * Used at facility initialization to cache schemas for tag info/edit panels.
   * @param {string} facilityId - The facility ID to load schemas for
   */
  async LOAD_SCHEMAS_FOR_FACILITY({ commit, state }, facilityId) {
    // Return cached data if already loaded
    if (state.schemasCacheLoaded) {
      return state.schemasCache;
    }

    if (!facilityId) {
      commit('SET_SCHEMAS_CACHE', {});
      return {};
    }

    try {
      const schemas = await masterImportService.getAllSchemas(facilityId, FACILITY_REQUEST);
      commit('SET_SCHEMAS_CACHE', schemas);
      return schemas;
    } catch (error) {
      debugWarn('[Masters] Failed to load schemas cache:', error);
      commit('SET_SCHEMAS_CACHE', {});
      return {};
    }
  },

  /**
   * Clear the schemas cache (e.g., when switching facilities).
   */
  CLEAR_SCHEMAS_CACHE({ commit }) {
    commit('CLEAR_SCHEMAS_CACHE');
  },

  /**
   * Create an empty batch (for API data source setup before any import).
   */
  async CREATE_EMPTY_BATCH({ commit }, request) {
    const result = await masterImportService.createEmptyBatch(request);
    return result;
  },

  // ==========================================================================
  // API Data Source Management
  // ==========================================================================

  /**
   * Fetch API data sources for a master batch.
   */
  async FETCH_API_DATA_SOURCES({ commit }, params = {}) {
    commit('SET_API_DATA_SOURCE_LOADING', true);
    try {
      const result = await masterApiDataSourceService.getDataSources(params);
      commit('SET_API_DATA_SOURCES', result.items || result || []);
      commit('SET_API_DATA_SOURCES_TOTAL', result.total || 0);
    } catch (err) {
      debugError('MASTERS', 'Failed to fetch API data sources:', err);
      commit('SET_API_DATA_SOURCES', []);
    } finally {
      commit('SET_API_DATA_SOURCE_LOADING', false);
    }
  },

  /**
   * Create a new API data source.
   */
  async CREATE_API_DATA_SOURCE({ commit }, data) {
    const result = await masterApiDataSourceService.createDataSource(data);
    commit('ADD_API_DATA_SOURCE', result);
    return result;
  },

  /**
   * Update an existing API data source.
   */
  async UPDATE_API_DATA_SOURCE({ commit }, { id, data }) {
    const result = await masterApiDataSourceService.updateDataSource(id, data);
    commit('UPDATE_API_DATA_SOURCE', result);
    return result;
  },

  /**
   * Delete an API data source.
   */
  async DELETE_API_DATA_SOURCE({ commit }, id) {
    await masterApiDataSourceService.deleteDataSource(id);
    commit('REMOVE_API_DATA_SOURCE', id);
  },

  /**
   * Trigger a manual data pull.
   */
  async TRIGGER_MANUAL_PULL(_, id) {
    return masterApiDataSourceService.triggerManualPull(id);
  },

  /**
   * Toggle schedule for a data source.
   */
  async TOGGLE_API_DATA_SOURCE_SCHEDULE(_, { id, enabled }) {
    return masterApiDataSourceService.toggleSchedule(id, enabled);
  },

  /**
   * Fetch execution history for a data source.
   */
  async FETCH_EXECUTION_HISTORY({ commit }, { dataSourceId, params }) {
    commit('SET_EXECUTION_HISTORY_LOADING', true);
    try {
      const result = await masterApiDataSourceService.getExecutionHistory(dataSourceId, params);
      commit('SET_EXECUTION_HISTORY', result.items || result || []);
      commit('SET_EXECUTION_HISTORY_TOTAL', result.total || 0);
    } catch (err) {
      debugError('MASTERS', 'Failed to fetch execution history:', err);
      commit('SET_EXECUTION_HISTORY', []);
    } finally {
      commit('SET_EXECUTION_HISTORY_LOADING', false);
    }
  },
};

// Helper functions have been moved to ./utils/importHelpers.js
// Import them at the top of this file as needed.